/**
 * Card Details Panel Layout - Shared slide-out panel shell
 */
import clsx from 'clsx';
import type { ReactElement, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IoArrowBack, IoClose } from 'react-icons/io5';

import useSlideOutPanel from '@/hooks/useSlideOutPanel';

export interface CardDetailsPanelLayoutProps {
  /** Whether the panel should be open */
  isOpen: boolean;
  /** Callback when panel should close */
  onClose: () => void;
  /** Back button label */
  backLabel: string;
  /** Optional aria-labelledby id */
  ariaLabelledBy?: string;
  /** Panel body content */
  children: ReactNode;
}

export default function CardDetailsPanelLayout({
  isOpen,
  onClose,
  backLabel,
  ariaLabelledBy,
  children,
}: CardDetailsPanelLayoutProps): ReactElement | null {
  const { shouldRender, isAnimating, panelRef, handleBackdropClick } = useSlideOutPanel({
    isOpen,
    onClose,
  });

  if (!shouldRender) return null;

  return createPortal(
    <div
      onClick={handleBackdropClick}
      className={clsx('fixed inset-0 z-50 flex justify-end transition-colors duration-200', isAnimating ? 'bg-void/60' : 'bg-transparent')}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
    >
      {/* Panel */}
      <div
        ref={panelRef}
        className={clsx(
          'bg-obsidian border-storm flex h-full w-full max-w-120 flex-col border-l shadow-2xl transition-transform duration-200 ease-out',
          isAnimating ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <header className="border-storm flex h-12 shrink-0 items-center justify-between border-b px-4">
          <button onClick={onClose} className="text-silver hover:text-pearl flex items-center gap-1.5 text-sm transition-colors">
            <IoArrowBack className="h-4 w-4" />
            {backLabel}
          </button>
          <button
            onClick={onClose}
            className="text-ash hover:text-pearl hover:bg-slate rounded-lg p-1.5 transition-colors"
            aria-label="Close panel"
          >
            <IoClose className="h-5 w-5" />
          </button>
        </header>

        {children}
      </div>
    </div>,
    document.body,
  );
}
